import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Collapse, Nav, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { LOCAL_STORAGE } from '@helpers';

import { SideNavProps } from './types';

export const SideNav: React.FunctionComponent<SideNavProps> = React.memo((props) => {
    const { id, onChange, defaultOpen, headerText, data, headerImage, cached, showToggle } = props;

    const SIDE_NAV = 'side-nav';

    const [sideMenuCollapsed, setSideMenuCollapsed] = useState(defaultOpen ?? false),
        [isCollapsed, setCollapsed] = useState(false);

    const activeItemChangeHandler = useCallback(
        (key: string | null) => {
            if (key) {
                onChange && onChange(key);
            }
        },
        [onChange],
    );

    useEffect(() => {
        if (cached) {
            const storage = LOCAL_STORAGE.getItem(SIDE_NAV);
            if (storage) {
                const { value } = JSON.parse(storage);
                sideMenuCollapsed && setSideMenuCollapsed(value);
            }
        }
    }, [cached, isCollapsed, sideMenuCollapsed]);

    const handleCollapseClick = () => {
        setSideMenuCollapsed((prev) => {
            const value = !prev;
            if (cached) {
                LOCAL_STORAGE.setItem(SIDE_NAV, JSON.stringify({ value }));
            }
            return !prev;
        });
    };

    const renderMenu = (data, level: number) => {
        // eslint-disable-next-line no-param-reassign
        level = level + 1;
        return (
            <div id="sidebar-wrapper" className={sideMenuCollapsed ? 'sb-sidenav-toggled sidebar-wrapper' : 'sidebar-wrapper'}>
                <div className="sidebar-menu position-relative">
                    {(headerText || headerImage) && (
                        <div className="sidebar-heading">
                            {headerImage && <img src={headerImage} alt="logo" className={headerText ? 'with-text' : ''} />}
                            {headerText && <span>{headerText}</span>}
                        </div>
                    )}
                    {showToggle && (
                        <Badge pill onClick={handleCollapseClick} className="position-absolute header-badge">
                            <i className={sideMenuCollapsed ? 'fas fa-arrow-right' : 'fas fa-arrow-left'} />
                        </Badge>
                    )}
                    <Nav as="ul" className="nav flex-column" id={id} onSelect={activeItemChangeHandler}>
                        {data
                            ? Object.keys(data).map((key) => {
                                  const item = data[key];
                                  return (
                                      <>
                                          {item.visible !== false && (
                                              <Nav.Item as="li" key={key} className={`sub-menu-${level}`}>
                                                  {item.children ? (
                                                      <>
                                                          <Nav.Link
                                                              eventKey={item.key}
                                                              aria-expanded={isCollapsed}
                                                              onClick={() => setCollapsed(!isCollapsed)}
                                                          >
                                                              {item.icon && <i className={item.icon} />}
                                                              {` ${item.text}`}
                                                              {item.icon && sideMenuCollapsed && (
                                                                  <OverlayTrigger
                                                                      placement="right"
                                                                      overlay={<Tooltip id={`tooltip-${item.key}`}>{item.text}</Tooltip>}
                                                                  >
                                                                      <i className={`${item.icon} fa-lg float-end pe-3`} />
                                                                  </OverlayTrigger>
                                                              )}
                                                          </Nav.Link>
                                                          <Collapse in={isCollapsed} className={'nav flex-column'}>
                                                              {renderMenu(item.children, level)}
                                                          </Collapse>
                                                      </>
                                                  ) : (
                                                      <Link to={item.path} key={item.key} className="nav-link">
                                                          {item.icon && <i className={item.icon} />}
                                                          {` ${item.text}`}
                                                          {item.icon && sideMenuCollapsed && showToggle && (
                                                              <OverlayTrigger
                                                                  placement="right"
                                                                  overlay={<Tooltip id={`tooltip-${item.key}`}>{item.text}</Tooltip>}
                                                              >
                                                                  <i className={`${item.icon} fa-lg float-end pe-3`} />
                                                              </OverlayTrigger>
                                                          )}
                                                      </Link>
                                                  )}
                                              </Nav.Item>
                                          )}
                                      </>
                                  );
                              })
                            : null}
                    </Nav>
                </div>
            </div>
        );
    };

    return renderMenu(data, 0);
});

SideNav.displayName = 'SideNav';
